import { useState, useEffect, useCallback } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetRevenueConcentrationUseCase } from "../../domain/use-cases/GetRevenueConcentrationUseCase";
import { formatPlAmount } from "../utils/formatPlAmount";
import type { RevenueConcentrationEntity } from "../../domain/entities/RevenueConcentrationEntity";
import {
  getRevenueBootstrapData,
  getRevenueBootstrapPromise,
} from "../state/revenueBootstrapStore";
import { getEffectiveDate } from "../../../../utils/effectiveDate";

export interface RevenueConcentrationRowItem {
  color: string;
  name: string;
  amount: string;
  percent: string;
  showConcentrationBadge?: boolean;
}

export interface RevenueConcentrationData {
  title: string;
  subtext: string;
  monthButtonLabel: string;
  totalRevenue: string;
  totalRevenueLabel: string;
  netInvoicedRevenue: string;
  netInvoicedRevenueLabel: string;
  unbilledRevenue: string;
  unbilledRevenueLabel: string;
  barSegments: { widthPercent: number; color: string; tooltip?: string }[];
  rows: RevenueConcentrationRowItem[];
  noteText: string;
}

const PALETTE = ["#d97706", "#6366f1", "#8b5cf6", "#a78bfa", "#c4b5fd"];
const OTHERS_COLOR = "#e5e7eb";

const repository = new ClientOverviewRepositoryImpl();
const getRevenueConcentrationUseCase = new GetRevenueConcentrationUseCase(
  repository,
);

function formatMonthYearForUi(rawLabel: string | null | undefined): string {
  const label = (rawLabel ?? "").trim();
  const format = (d: Date) =>
    new Intl.DateTimeFormat("en-US", {
      month: "long",
      year: "numeric",
    }).format(d);

  if (!label) return format(getEffectiveDate());

  // Formats to "March 2026". Handles a few common API label shapes.
  const yDashM = /^(\d{4})-(\d{1,2})$/;
  const mSlashY = /^(\d{1,2})[\/-](\d{4})$/;

  const yDashMMatch = label.match(yDashM);
  if (yDashMMatch) {
    const year = Number(yDashMMatch[1]);
    const month = Number(yDashMMatch[2]);
    if (Number.isFinite(year) && month >= 1 && month <= 12) {
      return format(new Date(year, month - 1, 1));
    }
  }

  const mSlashYMatch = label.match(mSlashY);
  if (mSlashYMatch) {
    const month = Number(mSlashYMatch[1]);
    const year = Number(mSlashYMatch[2]);
    if (Number.isFinite(year) && month >= 1 && month <= 12) {
      return format(new Date(year, month - 1, 1));
    }
  }

  // "Mar 2026", "March 2026", etc.
  const words = label.replace(/,/g, "").split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    const maybeYear = Number(words[words.length - 1]);
    if (Number.isFinite(maybeYear) && maybeYear >= 1900 && maybeYear <= 2100) {
      const monthWord = words[0].toLowerCase();
      const monthIndexByName: Record<string, number> = {
        jan: 1,
        january: 1,
        feb: 2,
        february: 2,
        mar: 3,
        march: 3,
        apr: 4,
        april: 4,
        may: 5,
        jun: 6,
        june: 6,
        jul: 7,
        july: 7,
        aug: 8,
        august: 8,
        sep: 9,
        sept: 9,
        september: 9,
        oct: 10,
        october: 10,
        nov: 11,
        november: 11,
        dec: 12,
        december: 12,
      };
      const month = monthIndexByName[monthWord];
      if (month) {
        return format(new Date(maybeYear, month - 1, 1));
      }
    }
  }

  return label;
}

function entityToDisplayData(
  entity: RevenueConcentrationEntity,
): RevenueConcentrationData {
  const netInvoiced = entity.totalRevenueAmount;
  const unbilled = entity.unbilledRevenueAmount;
  const total = netInvoiced + unbilled;
  const customers = entity.customers;
  const monthLabel = entity.monthLabel || "This month";
  const monthYearForUi = formatMonthYearForUi(monthLabel);

  const rows: RevenueConcentrationRowItem[] = customers.map((c, i) => {
    const color =
      c.name === "Others" ? OTHERS_COLOR : (PALETTE[i] ?? OTHERS_COLOR);
    return {
      color,
      name: c.name,
      amount: formatPlAmount(c.amount),
      percent: `${c.percent}%`,
      showConcentrationBadge: i === 0,
    };
  });

  const sumPositive = customers.reduce((s, c) => s + Math.max(0, c.amount), 0);

  const barSegments: {
    widthPercent: number;
    color: string;
    tooltip?: string;
  }[] = customers.map((c, i) => {
    const color =
      c.name === "Others" ? OTHERS_COLOR : (PALETTE[i] ?? OTHERS_COLOR);
    const amountStr = formatPlAmount(c.amount);
    const widthPercent =
      sumPositive > 0
        ? Math.round((1000 * Math.max(0, c.amount)) / sumPositive) / 10
        : 0;
    return {
      widthPercent,
      color,
      tooltip: `${c.name}: ${amountStr} (${c.percent}%)`,
    };
  });

  const totalRevenue = formatPlAmount(total);
  const netInvoicedRevenue = formatPlAmount(netInvoiced);
  // const first = customers[0];
  // const top2Pct =
  //   customers.length >= 2 ? (customers[0].percent + customers[1].percent).toFixed(1) : null;
  let noteText = "";
  // if (first && first.percent >= 35) {
  //   noteText = `${first.name} accounts for ${first.percent}% of revenue.${
  //     top2Pct ? ` Top 2 clients represent ${top2Pct}% — consider diversifying your pipeline.` : ""
  //   }`;
  // } else if (first && top2Pct && Number(top2Pct) >= 50) {
  //   noteText = `Top 2 clients represent ${top2Pct}% of revenue — consider diversifying your pipeline.`;
  // } else if (customers.length > 0) {
  //   noteText = "Revenue is well distributed across customers this month.";
  // }

  return {
    title: "Revenue Concentration",
    subtext: `Top Customer for the month of ${monthYearForUi}`,
    monthButtonLabel: monthLabel,
    totalRevenue,
    totalRevenueLabel: "Total revenue (Invoices + Journals)",
    netInvoicedRevenue,
    netInvoicedRevenueLabel: "Net invoiced revenue",
    unbilledRevenue: formatPlAmount(unbilled),
    unbilledRevenueLabel: "Unbilled revenue (Journals)",
    barSegments,
    rows,
    noteText,
  };
}

export function useRevenueConcentration(): RevenueConcentrationData & {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [entity, setEntity] = useState<RevenueConcentrationEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let bootstrapEntity = getRevenueBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getRevenueBootstrapPromise();
        if (bootstrapPromise) {
          try {
            bootstrapEntity = await bootstrapPromise;
          } catch {
            bootstrapEntity = null;
          }
        }
      }

      if (bootstrapEntity) {
        const section = bootstrapEntity.sections.revenueConcentration;
        if (section.status === "ok" && section.data) {
          setEntity(section.data);
          return;
        }
      }

      const data = await getRevenueConcentrationUseCase.execute();
      setEntity(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load revenue concentration",
      );
      setEntity(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const defaultData: RevenueConcentrationData = {
    title: "Revenue Concentration",
    subtext: `Top Customer for the month of ${formatMonthYearForUi(null)}`,
    monthButtonLabel: "—",
    totalRevenue: "—",
    totalRevenueLabel: "Total revenue (Invoices + Journals)",
    netInvoicedRevenue: "—",
    netInvoicedRevenueLabel: "Net invoiced revenue",
    unbilledRevenue: "—",
    unbilledRevenueLabel: "Unbilled revenue (Journals)",
    barSegments: [],
    rows: [],
    noteText: "",
  };

  const displayData = entity ? entityToDisplayData(entity) : defaultData;

  return {
    ...displayData,
    loading,
    error,
    refetch: load,
  };
}
